import { useEffect } from 'react';

/**
 * Hook for keyboard navigation in the image viewer
 * Handles arrow keys for prev/next and ESC/Enter for close
 * 
 * @param {Object} options
 * @param {Object} options.imageData - Current image data
 * @param {Object} options.currentSlideData - Current slide data (may be spread)
 * @param {number} options.spreadGap - Gap between spread images
 * @param {string} options.urlPrev - Pre-computed URL for previous navigation
 * @param {string} options.urlNext - Pre-computed URL for next navigation
 * @param {boolean} options.navIsFirst - Whether current position is first (screen-aware)
 * @param {boolean} options.navIsLast - Whether current position is last (screen-aware)
 * @param {boolean} options.isEditorial - Whether in editorial mode
 * @param {string} options.advanceUrl - URL to advance to after last image
 * @param {string} options.closeUrl - URL to close to
 * @param {Function} options.navigateWithFade - Function for fade navigation
 * @param {Function} options.closeWithFade - Function for fade close
 */
export const useKeyboardNavigation = ({
    imageData,
    currentSlideData,
    spreadGap,
    urlPrev,
    urlNext,
    navIsFirst,
    navIsLast,
    isEditorial,
    advanceUrl,
    closeUrl,
    navigateWithFade,
    closeWithFade,
}) => {
    useEffect(() => {
        const handleKeyboard = ({ keyCode, target }) => {
            if (!imageData) return;
            // Keys typed into text being edited belong to the text.
            if (target?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName || '')) return;
            
            const exitingPrev = navIsFirst;
            const exitingNext = navIsLast && advanceUrl;
            
            switch (keyCode) {
                case 27: // ESC
                    closeWithFade(closeUrl, true);
                    break;
                case 37: // Left arrow
                    // Skip if first image on editorial page (would cause redirect loop)
                    if (navIsFirst && isEditorial) break;
                    if (urlPrev) {
                        if (exitingPrev) {
                            closeWithFade(urlPrev);
                        } else {
                            navigateWithFade(urlPrev, 'prev', imageData, currentSlideData, spreadGap);
                        }
                    }
                    break;
                case 39: // Right arrow
                    if (urlNext) {
                        if (exitingNext) {
                            closeWithFade(urlNext);
                        } else {
                            navigateWithFade(urlNext, 'next', imageData, currentSlideData, spreadGap);
                        }
                    }
                    break;
                case 38: // Up arrow
                case 40: // Down arrow
                case 13: // Enter/Return
                    closeWithFade(closeUrl, true);
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyboard);
        return () => document.removeEventListener('keydown', handleKeyboard);
    }, [
        imageData, 
        currentSlideData,
        spreadGap,
        urlPrev, 
        urlNext, 
        navIsFirst, 
        navIsLast, 
        advanceUrl, 
        closeUrl, 
        navigateWithFade, 
        closeWithFade, 
        isEditorial
    ]);
};

export default useKeyboardNavigation;
